import { useEffect } from "react";
import {
  Link,
  createFileRoute,
  getRouteApi,
  notFound,
} from "@tanstack/react-router";
import { trackPageView } from "@/hooks/useAnalytics";
import BreadcrumbJsonLd from "@/components/common/breadcrumbJsonLd";
import NotFoundPage from "@/components/common/NotFoundPage";
import TagPill from "@/components/common/tagPill";
import { jsonLdSafe } from "@/lib/jsonLd";
import { CATEGORY_LABELS } from "@/lib/categoryLabels";
import type { JobCategoryWithTags, JobTag } from "@/types/db";

const SITE_URL = "https://datafirstjobs.com";
const rootRoute = getRouteApi("__root__");

export const Route = createFileRoute("/browse/$slug")({
  loader: async ({ params, context }) => {
    const category = context.jobCategoriesWithTags.find(
      (c: JobCategoryWithTags) => c.slug === params.slug,
    );
    if (!category || category.jobTags.length === 0) throw notFound();
    return { category };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const label =
      CATEGORY_LABELS[loaderData.category.slug] ?? loaderData.category.name;
    const count = loaderData.category.jobTags.length;
    const title = `All ${label} · Browse data jobs | Data First Jobs`;
    const description = `Browse every ${label.toLowerCase()} category on Data First Jobs — ${count} filters to narrow your search for data engineering, analytics, and data science roles.`;
    const url = `${SITE_URL}/browse/${loaderData.category.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BrowseCategoryPage,
  notFoundComponent: NotFoundPage,
});

function BrowseCategoryPage() {
  const { category } = Route.useLoaderData();
  const { jobCategoriesWithTags } = rootRoute.useRouteContext();

  useEffect(() => {
    trackPageView("browse_category", category.slug);
  }, [category.slug]);

  const label = CATEGORY_LABELS[category.slug] ?? category.name;
  const accent = category.bgColor ?? "bg-gray-200";

  // Offer jumps to sibling categories at the bottom for cross-discovery.
  const siblings = jobCategoriesWithTags.filter(
    (c: JobCategoryWithTags) =>
      c.slug !== category.slug && c.jobTags.length > 0,
  );

  return (
    <>
      <BreadcrumbJsonLd
        items={[{ name: "Home", url: SITE_URL }, { name: `Browse ${label}` }]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: jsonLdSafe({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `All ${label}`,
            url: `${SITE_URL}/browse/${category.slug}`,
            hasPart: category.jobTags.map((tag: JobTag) => ({
              "@type": "WebPage",
              name: `${tag.name} Jobs`,
              url: `${SITE_URL}/${tag.slug}`,
            })),
          }),
        }}
      />

      <section className="mx-auto mt-16 max-w-4xl px-4 text-center sm:mt-20 sm:px-6 lg:px-8">
        <p className="mx-auto mb-4 font-mono text-xs tracking-wide text-gray-500 uppercase tabular-nums">
          Browse · {category.jobTags.length} options
        </p>
        <h1 className="mx-auto mb-5 max-w-[20ch] text-4xl font-semibold tracking-tight text-balance text-gray-900 md:text-6xl lg:text-7xl">
          All <span className="text-polaris-orange">{label}</span>
        </h1>
        <p className="mx-auto my-2 max-w-[56ch] text-lg text-pretty text-gray-500 md:text-xl">
          Every {label.toLowerCase()} filter on Data First Jobs. Pick one to see
          roles tagged with it across the site.
        </p>
      </section>

      <section className="mx-auto mt-14 max-w-5xl px-4 sm:px-6 lg:px-8">
        <ul className="flex flex-wrap justify-center gap-2" role="list">
          {category.jobTags.map((tag: JobTag) => (
            <li key={tag.id}>
              <TagPill
                variant="filter"
                size="md"
                bgColor={accent}
                to="/$slug"
                params={{ slug: tag.slug }}
              >
                {tag.name}
              </TagPill>
            </li>
          ))}
        </ul>
      </section>

      {siblings.length > 0 && (
        <section className="mx-auto mt-20 max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-6 text-center text-xl font-semibold tracking-tight text-gray-800">
            Browse other categories
          </h2>
          <ul
            className="flex flex-wrap justify-center gap-3 text-sm"
            role="list"
          >
            {siblings.map((c: JobCategoryWithTags) => (
              <li key={c.slug}>
                <Link
                  to="/browse/$slug"
                  params={{ slug: c.slug }}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-white px-4 py-2 text-gray-700 shadow-sm transition-all duration-150 hover:border-gray-300 hover:shadow-md"
                >
                  {CATEGORY_LABELS[c.slug] ?? c.name}
                  <span className="ml-2 text-gray-400 tabular-nums">
                    {c.jobTags.length}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
